/**
 * Assistance Request controls (AR.5) — the operator entry point for the PEOC
 * routing workflow, rendered in the CommandBar's REQUEST group under the SITREP
 * controls. Hosts the "New request" button → AssistanceRequestForm modal.
 *
 * The triage QUEUE moved out of this command-bar dropdown into the LeftRail
 * Coordination → Assist worklist (#378) — a cramped 320px popover was the wrong
 * home for a persistent, lifecycle-tracked worklist (design-council PART IX). After
 * a request is submitted here, `requestAssistRefresh()` nudges that worklist to
 * surface it at once (it otherwise appears on the next 30s poll). The server owns
 * all audit logging (submit / view / status / forward).
 */
import { useState } from 'react';
import { AssistanceRequestForm } from './AssistanceRequestForm';
import type { SitrepIncidentSelection } from './SitrepControls';
import { showBarCaptions } from './lib/commandBarLayout';
import { useCommandBarTier } from './lib/commandBarTier';
import { requestAssistRefresh } from './assistBridge';

export function AssistanceControls({
  selectedIncident,
}: {
  selectedIncident: SitrepIncidentSelection | null;
}): JSX.Element {
  const captions = showBarCaptions(useCommandBarTier());
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      <div
        data-testid="assistance-controls"
        className="flex shrink-0 items-center gap-2 whitespace-nowrap"
      >
        {captions && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Request
          </span>
        )}
        <button
          type="button"
          data-testid="new-assistance-btn"
          onClick={() => setShowForm(true)}
          className="text-xs py-1 px-3 rounded bg-amber-600/90 hover:bg-amber-600 border border-amber-500 text-white font-medium"
        >
          New request
        </button>
      </div>

      {showForm && (
        <AssistanceRequestForm
          selectedIncident={selectedIncident}
          onClose={() => setShowForm(false)}
          onSubmitted={() => {
            // The form already toasts success; nudge the Coordination → Assist
            // worklist to refetch so the new request appears immediately.
            setShowForm(false);
            requestAssistRefresh();
          }}
        />
      )}
    </>
  );
}
